// src/components/Sidebar.js - VERSION COMPLÈTE
import React, { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

const Sidebar = () => {
  const [user, setUser] = useState(null);
  const [pendingValidationsCount, setPendingValidationsCount] = useState(0);
  const location = useLocation();

  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (stored) {
      try {
        setUser(JSON.parse(stored));
      } catch (e) {
        // Pas de console.log
      }
    }
  }, []);

  // Number(...) est indispensable ici : le backend ne garantit pas toujours
  // que `profile` soit une string ou un nombre selon l'endpoint qui a rempli
  // le localStorage — sans cette coercion, une comparaison stricte (=== 1,
  // === 3...) échoue silencieusement et TOUT le menu disparaît pour tout le
  // monde (même bug que celui déjà corrigé dans UsersList.js).
  const userProfile = Number(user?.profile || user?.poste || 0);

  // Badge "Validations" : nombre de sociétés en attente, visible en permanence
  // dans le menu (pas seulement quand le tableau de bord est ouvert).
  useEffect(() => {
    if (userProfile !== 1) return;
    const token = localStorage.getItem("access_token");
    if (!token) return;

    axios.get(`${API_BASE_URL}/users/activation.php?status=pending`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      setPendingValidationsCount(res.data?.data?.users?.length || 0);
    }).catch(() => {});
  }, [userProfile]);
  
  // Permissions
  // Utilisateurs/Sociétés : gestion administrative, jamais le contrôleur (backend
  // users/list.php et societes/* ne l'autorisent pas non plus).
  const canSeeUsers = userProfile === 1 || userProfile === 3;
  const canSeeSocietes = userProfile === 1 || userProfile === 3;
  const canSeeClients = userProfile === 3 || userProfile === 2; // Gestionnaires ET Contrôleurs
  const canSeeSites = userProfile === 3 || userProfile === 2; // Gestionnaires ET Contrôleurs
  const canSeeAffectations = userProfile === 3 || userProfile === 2; // Gestionnaires ET Contrôleurs
  // Affectation des contrôleurs aux sites : action réservée au gestionnaire
  // (affectations/controleur-create.php et delete-controleur.php exigent
  // profile === 3) ; le contrôleur n'a ici aucune action possible.
  const canSeeAffectationsControleurs = userProfile === 3;
  const canSeeAgents = userProfile === 3; // Gestionnaires seulement
  const canSeeActivation = userProfile === 1; // Admin seulement
  // Poste de contrôle (5) ajouté : ces pages n'ont aucun blocage côté page,
  // seul le menu les cachait (le profil pouvait déjà y accéder par URL).
  // Agent (4) ajouté : main_courante/create.php, list.php et my_list.php sont
  // en réalité conçus pour l'agent (il crée ses propres mains courantes) ;
  // le menu le lui cachait alors que le backend l'autorise pleinement.
  const canSeeMainCourante = userProfile === 3 || userProfile === 5 || userProfile === 4;
  
  // Permissions pour les tutoriels
  const canSeePublicTutos = userProfile === 1 || userProfile === 3 || userProfile === 2 || userProfile === 4 || userProfile === 5; // Tous les profils
  const canSeeAdminTutos = userProfile === 1; // Seulement admin
  
  // Permissions pour Stocks, Incidents, Tags, Rondes (Gestionnaires seulement)
  const canSeeStocks = userProfile === 3; // Uniquement Gestionnaires
  // Incidents et Rondes : poste de contrôle (5) ajouté, même raison que
  // canSeeMainCourante ci-dessus.
  const canSeeIncidents = userProfile === 3 || userProfile === 5;
  const canSeeTags = userProfile === 3; // Uniquement Gestionnaires
  const canSeeRondes = userProfile === 3 || userProfile === 5;
  
  // Permissions pour Tâches — le contrôleur (2) est volontairement exclu :
  // taches/list.php ne gère que les profils gestionnaire (3) et agent (4) et
  // renvoie 403 "Profil non autorisé" pour tout autre profil ; l'afficher au
  // contrôleur ne menait qu'à un message "Accès interdit".
  const canSeeTaches = userProfile === 3 || userProfile === 4; // Gestionnaires et Agents

  // Vérifier si l'utilisateur peut voir AU MOINS UN élément du menu Sites
  const canSeeSiteSection = canSeeSites || canSeeAffectations || canSeeAffectationsControleurs || canSeeAgents;

  // Vérifier si on est sur certaines pages pour ouvrir automatiquement les collapses
  const isSitesActive = location.pathname.startsWith('/sites') || 
                       location.pathname.startsWith('/affectations') || 
                       location.pathname.startsWith('/affectations-controleurs') ||
                       location.pathname.startsWith('/agents');

  const isIncidentsActive = location.pathname.startsWith('/incidents') || 
                           location.pathname.startsWith('/typeIncidents');

  const isRondesActive = location.pathname.startsWith('/rondes');
  
  // Vérifier si on est sur les pages Stocks
  const isStocksActive = location.pathname.startsWith('/products') || 
                         location.pathname.startsWith('/entrer-stock') || 
                         location.pathname.startsWith('/sortir-stock') ||
                         location.pathname.startsWith('/product-types');
  
  // Vérifier si on est sur les pages Tâches
  const isTachesActive = location.pathname.startsWith('/taches');

  const isTutosActive = location.pathname.startsWith('/tutos');

  const isAbonnementsActive = location.pathname.startsWith('/abonnements');

  return (
    <aside id="sidebar" className="sidebar">
      <ul className="sidebar-nav" id="sidebar-nav">
        
        {/* 1. Dashboard */}
        <li className="nav-item">
          <NavLink 
            className={({ isActive }) => `nav-link ${isActive ? "" : "collapsed"}`}
            to="/dashboard"
          >
            <i className="bi bi-grid"></i>
            <span>Tableau de bord</span>
          </NavLink>
        </li>

        {/* 1bis. Poste de Contrôle — centre d'opérations : Gestionnaire (supervision) et Poste de contrôle (écran principal) */}
        {(userProfile === 3 || userProfile === 5) && (
          <li className="nav-item">
            <NavLink
              className={({ isActive }) => `nav-link ${isActive ? "" : "collapsed"}`}
              to="/poste-controle"
            >
              <i className="bi bi-broadcast"></i>
              <span>Poste de Contrôle</span>
            </NavLink>
          </li>
        )}

        {/* 3bis. Validations (sociétés en attente) — admin uniquement */}
        {canSeeActivation && (
          <li className="nav-item">
            <NavLink
              className={({ isActive }) => `nav-link d-flex align-items-center ${isActive ? "" : "collapsed"}`}
              to="/activation"
            >
              <i className="bi bi-patch-check"></i>
              <span>Validations</span>
              {pendingValidationsCount > 0 && (
                <span className="badge bg-danger rounded-pill ms-auto">
                  {pendingValidationsCount}
                </span>
              )}
            </NavLink>
          </li>
        )}

        {/* 4. Clients */}
        {canSeeClients && (
          <li className="nav-item">
            <NavLink 
              className={({ isActive }) => `nav-link ${isActive ? "" : "collapsed"}`}
              to="/clients"
            >
              <i className="bi bi-person-badge"></i>
              <span>Clients</span>
            </NavLink>
          </li>
        )}

        {/* 5. Sites */}
        {canSeeSiteSection && (
          <li className="nav-item">
            <a 
              className={`nav-link ${isSitesActive ? "" : "collapsed"}`}
              data-bs-toggle="collapse"
              href="#sites-nav"
            >
              <i className="bi bi-buildings"></i>
              <span>Sites</span>
              <i className="bi bi-chevron-down ms-auto"></i>
            </a>
            
            <ul id="sites-nav" className={`nav-content collapse ${isSitesActive ? "show" : ""}`} data-bs-parent="#sidebar-nav">
              {/* Sites - pour Gestionnaires et Contrôleurs */}
              {canSeeSites && (
                <>
                  <li>
                    <NavLink 
                      to="/sites"
                      className={({ isActive }) => isActive ? "active" : ""}
                    >
                      <i className="bi bi-circle"></i>
                      <span>Listes</span>
                    </NavLink>
                  </li>
                  
                  <li>
                    <NavLink 
                      to="/sites/map"
                      className={({ isActive }) => isActive ? "active" : ""}
                    >
                      <i className="bi bi-circle"></i>
                      <span>Carte des Sites</span>
                    </NavLink>
                  </li>
                </>
              )}
              
              {/* Affectations Agents - pour Gestionnaires et Contrôleurs */}
              {canSeeAffectations && (
                <li>
                  <NavLink 
                    to="/affectations"
                    className={({ isActive }) => isActive ? "active" : ""}
                  >
                    <i className="bi bi-circle"></i>
                    <span>Affectations Agents</span>
                  </NavLink>
                </li>
              )}
              {/* Affectations Contrôleurs - pour Gestionnaires et Contrôleurs */}
              {canSeeAffectationsControleurs && (
                <li>
                  <NavLink
                    to="/affectations-controleurs"
                    className={({ isActive }) => (isActive ? "active" : "")}
                  >
                    <i className="bi bi-circle"></i>
                    <span>Affectations Contrôleurs</span>
                  </NavLink>
                </li>
              )}
              {/* Planning - pour Gestionnaires seulement */}
              {userProfile === 3 && (
                <li>
                  <NavLink
                    to="/planning"
                    className={({ isActive }) => (isActive ? "active" : "")}
                  >
                    <i className="bi bi-circle"></i>
                    <span>Planning</span>
                  </NavLink>
                </li>
              )}
            </ul>
          </li>
        )}

        {/* 6. Main Courante - Gestionnaires, Poste de contrôle et Agents */}
        {canSeeMainCourante && (
          <li className="nav-item">
            <NavLink 
              to="/maincourante" 
              className={({ isActive }) => `nav-link ${isActive ? "" : "collapsed"}`}
            >
              <i className="bi bi-journal-text"></i>
              <span>Main courante</span>
            </NavLink>
          </li>
        )}

        {/* 7. Tâches - Pour Gestionnaires, Contrôleurs et Agents */}
        {canSeeTaches && (
          <li className="nav-item">
            <a 
              className={`nav-link ${isTachesActive ? "" : "collapsed"}`}
              data-bs-toggle="collapse"
              href="#taches-nav"
            >
              <i className="bi bi-list-task"></i>
              <span>Tâches</span>
              <i className="bi bi-chevron-down ms-auto"></i>
            </a>

            <ul 
              id="taches-nav" 
              className={`nav-content collapse ${isTachesActive ? "show" : ""}`}
              data-bs-parent="#sidebar-nav"
            >
              <li>
                <NavLink 
                  to="/taches"
                  className={({ isActive }) => isActive ? "active" : ""}
                >
                  <i className="bi bi-circle"></i>
                  <span>Liste des tâches</span>
                </NavLink>
              </li>
            </ul>
          </li>
        )}

        {/* 8. Rondes - Uniquement pour Gestionnaires */}
        {canSeeRondes && (
          <li className="nav-item">
            <a 
              className={`nav-link ${isRondesActive ? "" : "collapsed"}`}
              data-bs-toggle="collapse"
              href="#rondes-nav"
            >
              <i className="bi bi-shield-check"></i>
              <span>Rondes</span>
              <i className="bi bi-chevron-down ms-auto"></i>
            </a>
            
            <ul id="rondes-nav" className={`nav-content collapse ${isRondesActive ? "show" : ""}`} data-bs-parent="#sidebar-nav">
              <li>
                <NavLink 
                  to="/rondes"
                  className={({ isActive }) => isActive ? "active" : ""}
                >
                  <i className="bi bi-circle"></i>
                  <span>Liste des rondes</span>
                </NavLink>
              </li>
            </ul>
          </li>
        )}

        {/* 9. Tags - Uniquement pour Gestionnaires (avec les opérations courantes) */}
        {canSeeTags && (
          <li className="nav-item">
            <NavLink 
              className={({ isActive }) => `nav-link ${isActive ? "" : "collapsed"}`}
              to="/tags"
            >
              <i className="bi bi-tags"></i>
              <span>Tags</span>
            </NavLink>
          </li>
        )}

        {/* 10. Incidents - Uniquement pour Gestionnaires */}
        {canSeeIncidents && (
          <li className="nav-item">
            <a 
              className={`nav-link ${isIncidentsActive ? "" : "collapsed"}`}
              data-bs-toggle="collapse"
              href="#incidents-nav"
            >
              <i className="bi bi-exclamation-triangle"></i>
              <span>Incidents</span>
              <i className="bi bi-chevron-down ms-auto"></i>
            </a>
            
            <ul id="incidents-nav" className={`nav-content collapse ${isIncidentsActive ? "show" : ""}`} data-bs-parent="#sidebar-nav">
              <li>
                <NavLink 
                  to="/incidents"
                  className={({ isActive }) => isActive ? "active" : ""}
                >
                  <i className="bi bi-circle"></i>
                  <span>Liste des incidents</span>
                </NavLink>
              </li>
              
              {/* Type d'incidents */}
              <li>
                <NavLink 
                  to="/typeIncidents"
                  className={({ isActive }) => isActive ? "active" : ""}
                >
                  <i className="bi bi-circle"></i>
                  <span>Type d'incidents</span>
                </NavLink>
              </li>
            </ul>
          </li>
        )}

        {/* 11. Stocks - Uniquement pour Gestionnaires */}
        {canSeeStocks && (
          <li className="nav-item">
            <a 
              className={`nav-link ${isStocksActive ? "" : "collapsed"}`}
              data-bs-toggle="collapse"
              href="#stocks-nav"
            >
              <i className="bi bi-box-seam"></i>
              <span>Stocks</span>
              <i className="bi bi-chevron-down ms-auto"></i>
            </a>

            <ul 
              id="stocks-nav" 
              className={`nav-content collapse ${isStocksActive ? "show" : ""}`}
              data-bs-parent="#sidebar-nav"
            >
              <li>
                <NavLink 
                  to="/products"
                  className={({ isActive }) => isActive ? "active" : ""}
                >
                  <i className="bi bi-circle"></i>
                  <span>Produits</span>
                </NavLink>
              </li>
              <li>
                <NavLink 
                  to="/entrer-stock"
                  className={({ isActive }) => isActive ? "active" : ""}
                >
                  <i className="bi bi-circle"></i>
                  <span>Entrées de stock</span>
                </NavLink>
              </li>
              <li>
                <NavLink 
                  to="/sortir-stock"
                  className={({ isActive }) => isActive ? "active" : ""}
                >
                  <i className="bi bi-circle"></i>
                  <span>Sorties de stock</span>
                </NavLink>
              </li>
              <li>
                <NavLink 
                  to="/product-types"
                  className={({ isActive }) => isActive ? "active" : ""}
                >
                  <i className="bi bi-circle"></i>
                  <span>Type Produits</span>
                </NavLink>
              </li>
            </ul>
          </li>
        )}

        {/* 12. Offres d'abonnement - Uniquement pour Administrateur */}
        {userProfile === 1 && (
          <li className="nav-item">
            <NavLink 
              className={({ isActive }) => `nav-link ${isActive ? "" : "collapsed"}`}
              to="/offres"
            >
              <i className="bi bi-tags"></i>
              <span>Offres d'abonnement</span>
            </NavLink>
          </li>
        )}

        {/* 13. Abonnements - Pour Administrateur et Gestionnaire */}
        {(userProfile === 1 || userProfile === 3) && (
          <li className="nav-item">
            <NavLink
              className={({ isActive }) => `nav-link ${isActive ? "" : "collapsed"}`}
              to="/abonnements"
            >
              <i className="bi bi-calendar-check"></i>
              <span>Abonnements</span>
            </NavLink>
          </li>
        )}

        {/* 13a. Achat de quotas / Historique - jusqu'ici accessibles uniquement
            par lien profond (aucune entrée de menu), pour Admin et Gestionnaire
            comme achat_quota/create.php. */}
        {(userProfile === 1 || userProfile === 3) && (
          <li className="nav-item">
            <NavLink
              className={({ isActive }) => `nav-link ${isActive ? "" : "collapsed"}`}
              to="/quotas/list"
            >
              <i className="bi bi-basket"></i>
              <span>Achats de quotas</span>
            </NavLink>
          </li>
        )}

        {/* 13bis. Rapports - L'admin ne gère ni stock ni incidents (données
            opérationnelles propres à chaque société) : il ne voit que les
            rapports transversaux abonnements/quotas. Le gestionnaire garde
            l'intitulé générique puisqu'il a accès aux 4 domaines. */}
        {userProfile === 1 && (
          <li className="nav-item">
            <NavLink
              className={({ isActive }) => `nav-link ${isActive ? "" : "collapsed"}`}
              to="/rapports"
            >
              <i className="bi bi-file-earmark-bar-graph"></i>
              <span>Rapports (abonnements, quotas)</span>
            </NavLink>
          </li>
        )}
        {userProfile === 3 && (
          <li className="nav-item">
            <NavLink
              className={({ isActive }) => `nav-link ${isActive ? "" : "collapsed"}`}
              to="/rapports"
            >
              <i className="bi bi-file-earmark-bar-graph"></i>
              <span>Rapports (stock, incidents...)</span>
            </NavLink>
          </li>
        )}

        {/* 13bis2. Rapport client (activité globale) - Gestionnaire uniquement */}
        {userProfile === 3 && (
          <li className="nav-item">
            <NavLink
              className={({ isActive }) => `nav-link ${isActive ? "" : "collapsed"}`}
              to="/rapports/client"
            >
              <i className="bi bi-file-earmark-text"></i>
              <span>Rapport d'activité client</span>
            </NavLink>
          </li>
        )}

        {/* 13ter. Paramètres de paiement (NITA/AMANA) - Admin uniquement */}
        {userProfile === 1 && (
          <li className="nav-item">
            <NavLink
              className={({ isActive }) => `nav-link ${isActive ? "" : "collapsed"}`}
              to="/parametres-paiement"
            >
              <i className="bi bi-credit-card"></i>
              <span>Paiement (NITA/AMANA)</span>
            </NavLink>
          </li>
        )}

        {/* 13ter1. Tarifs des quotas (client/site/tag_qr/tag_nfc) - Admin uniquement */}
        {userProfile === 1 && (
          <li className="nav-item">
            <NavLink
              className={({ isActive }) => `nav-link ${isActive ? "" : "collapsed"}`}
              to="/parametres-quotas"
            >
              <i className="bi bi-tags"></i>
              <span>Tarifs des quotas</span>
            </NavLink>
          </li>
        )}

        {/* 13ter2. Historique des paiements NITA/Amana - Admin et Gestionnaire */}
        {(userProfile === 1 || userProfile === 3) && (
          <li className="nav-item">
            <NavLink
              className={({ isActive }) => `nav-link ${isActive ? "" : "collapsed"}`}
              to="/historique-paiements"
            >
              <i className="bi bi-receipt"></i>
              <span>Historique des paiements</span>
            </NavLink>
          </li>
        )}

        {/* 13ter3. Journaux (historique d'activité) - Admin, Gestionnaire et Poste de contrôle */}
        {(userProfile === 1 || userProfile === 3 || userProfile === 5) && (
          <li className="nav-item">
            <NavLink
              className={({ isActive }) => `nav-link ${isActive ? "" : "collapsed"}`}
              to="/journaux"
            >
              <i className="bi bi-journal-text"></i>
              <span>Journaux</span>
            </NavLink>
          </li>
        )}

        {/* 13quater. Retraits partenaires - Admin uniquement */}
        {userProfile === 1 && (
          <li className="nav-item">
            <NavLink
              className={({ isActive }) => `nav-link ${isActive ? "" : "collapsed"}`}
              to="/retraits-partenaires"
            >
              <i className="bi bi-cash-stack"></i>
              <span>Retraits partenaires</span>
            </NavLink>
          </li>
        )}

        {/* 13quinquies. Utilisateurs — rarement modifié, en bas du menu */}
        {canSeeUsers && (
          <li className="nav-item">
            <NavLink
              className={({ isActive }) => `nav-link ${isActive ? "" : "collapsed"}`}
              to="/users"
            >
              <i className="bi bi-people"></i>
              <span>Utilisateurs</span>
            </NavLink>
          </li>
        )}

        {/* 13sexies. Sociétés — rarement modifié, en bas du menu */}
        {canSeeSocietes && (
          <li className="nav-item">
            <NavLink
              className={({ isActive }) => `nav-link ${isActive ? "" : "collapsed"}`}
              to="/societes"
            >
              <i className="bi bi-building"></i>
              <span>Sociétés</span>
            </NavLink>
          </li>
        )}

        {/* 14. Tutoriels - Menu avec sous-menu */}
        {(canSeePublicTutos || canSeeAdminTutos) && (
          <li className="nav-item">
            <a 
              className={`nav-link ${isTutosActive ? "" : "collapsed"}`}
              data-bs-toggle="collapse"
              href="#tutos-nav"
            >
              <i className="bi bi-collection-play"></i>
              <span>Tutoriels</span>
              <i className="bi bi-chevron-down ms-auto"></i>
            </a>
            
            <ul id="tutos-nav" className={`nav-content collapse ${isTutosActive ? "show" : ""}`} data-bs-parent="#sidebar-nav">
              {/* Lien PUBLIC pour tout le monde */}
              <li>
                <NavLink 
                  to="/tutos"
                  className={({ isActive }) => isActive ? "active" : ""}
                >
                  <i className="bi bi-eye"></i>
                  <span>Voir les tutoriels</span>
                </NavLink>
              </li>
              
              {/* Lien ADMIN seulement pour les administrateurs */}
              {canSeeAdminTutos && (
                <li>
                  <NavLink 
                    to="/tutos/admin"
                    className={({ isActive }) => isActive ? "active" : ""}
                  >
                    <i className="bi bi-shield-check"></i>
                    <span>Gérer les tutoriels</span>
                  </NavLink>
                </li>
              )}
            </ul>
          </li>
        )}
      </ul>
    </aside>
  );
};

export default Sidebar;